import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl, gbp, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCheckout } from "@/hooks/useCheckout";
import { TrustBadge } from "@/components/TrustBadge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Loader2, CheckCircle2, Trash2, Tag, CreditCard, Gift, ImageOff, AlertCircle, Rocket } from "lucide-react";

const PLAN_NAMES = { private_monthly: "Private £9.99/month", dealer_locations: "Dealership plan" };
const fmt = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
const future = (iso) => iso && new Date(iso) > new Date();

function PlanCard({ sub, isDealer }) {
  const label = sub?.active ? PLAN_NAMES[sub.plan] : sub?.free_until ? "Free period" : "No active plan";
  return (
    <div className="bg-white rounded-2xl border border-navy/10 p-5">
      <div className="flex items-center gap-2 text-slatev text-sm"><CreditCard className="w-4 h-4" />Plan</div>
      <div className="font-heading text-xl font-bold mt-2 text-navy" data-testid="sub-status">{label}</div>
      {sub?.free_until && !sub?.active && <p className="text-xs text-slatev mt-1" data-testid="free-until">Free until {fmt(sub.free_until)}</p>}
      {!sub?.active && <Link to={isDealer ? "/pricing/dealers" : "/pricing/private"} className="text-sm text-teal font-semibold" data-testid="choose-plan-link">{sub?.free_until ? "Set up £9.99 for later →" : "Choose a plan →"}</Link>}
      {sub?.plan === "dealer_locations" && <p className="text-xs text-slatev mt-1" data-testid="dealer-count">{sub.quantity} dealership{sub.quantity > 1 ? "s" : ""} · unlimited cars</p>}
      {sub?.plan === "dealer_locations" && <Link to="/pricing/dealers" className="text-sm text-teal font-semibold" data-testid="change-dealerships-link">Change dealerships →</Link>}
    </div>
  );
}

function BoostMenu({ l, busy, checkout }) {
  const opts = [
    ["extra_feature", "Feature for 1 week · £4.99"],
    ["extra_recommend", "Home page pick, 7 days · £9.99"],
    ...(l.expires_at ? [["extra_extend", "Extend by 1 week · £4.99"]] : []),
  ];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" className="bg-teal hover:bg-teal-dark text-white" data-testid={`boost-${l.id}`}>
          {busy?.endsWith(l.id) ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Rocket className="w-4 h-4 mr-1" />Boost</>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white w-64">
        {opts.map(([k, label]) => <DropdownMenuItem key={k} onClick={() => checkout(k, { listing_id: l.id })} data-testid={`boost-${k}-${l.id}`}>{label}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ListingRow({ l, act, busy, checkout }) {
  return (
    <div className="bg-white rounded-2xl border border-navy/10 p-4 flex flex-wrap sm:flex-nowrap items-center gap-4" data-testid={`dashboard-listing-${l.id}`}>
      <div className="w-20 h-16 rounded-xl overflow-hidden bg-teal-tint shrink-0">
        {l.photos?.[0] ? <img src={fileUrl(l.photos[0])} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slatev/40"><ImageOff className="w-5 h-5" /></div>}
      </div>
      <div className="flex-1 min-w-0">
        <Link to={`/listing/${l.id}`} className="font-heading font-semibold truncate text-navy hover:text-teal">{l.make} {l.model}</Link>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <TrustBadge type={l.status} />
          {l.verified ? <TrustBadge type="verified" /> : l.data_checked && <TrustBadge type="data_checked" />}
          {l.mileage_warning && <TrustBadge type="mileage" />}
          {future(l.featured_until) && <span className="text-xs font-semibold text-amber-700" data-testid={`featured-until-${l.id}`}>Featured to {fmt(l.featured_until)}</span>}
          {future(l.recommended_until) && <span className="text-xs font-semibold text-teal" data-testid={`recommended-until-${l.id}`}>Home page to {fmt(l.recommended_until)}</span>}
          <span className="text-sm text-slatev">{gbp(l.price)}</span>
          {l.expires_at && l.status === "live" && <span className="text-xs text-slatev" data-testid={`expires-${l.id}`}>Live until {fmt(l.expires_at)}</span>}
        </div>
        {l.hold_reason && <p className="text-xs text-amber-700 mt-1">{l.hold_reason}</p>}
      </div>
      <div className="flex gap-2 shrink-0">
        {l.status === "live" && <BoostMenu l={l} busy={busy} checkout={checkout} />}
        {["live", "pending_review", "held"].includes(l.status) && <Button size="sm" variant="outline" onClick={() => act(l.id, "sold")} data-testid={`mark-sold-${l.id}`}><CheckCircle2 className="w-4 h-4 mr-1" />Sold</Button>}
        {l.status !== "removed" && <Button size="sm" variant="ghost" onClick={() => act(l.id, "remove")} data-testid={`remove-${l.id}`}><Trash2 className="w-4 h-4" /></Button>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { busy, checkout } = useCheckout();
  const [listings, setListings] = useState(null);
  const [sub, setSub] = useState(null);

  const load = () => {
    api.get("/listings/mine").then(({ data }) => setListings(data)).catch(() => setListings([]));
    api.get("/subscription").then(({ data }) => setSub(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try { await api.post(`/listings/${id}/${action}`); toast.success("Updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const cancelSub = async () => {
    try { await api.post("/subscription/cancel"); toast.success("Plan cancelled. Listings taken offline immediately."); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="text-xs uppercase tracking-widest font-semibold text-teal">My garage</span>
          <h1 className="font-heading font-extrabold text-navy tracking-tighter text-4xl mt-2">Welcome back, {user?.name?.split(" ")[0]}.</h1>
        </div>
        <Button asChild className="rounded-full bg-teal hover:bg-teal-dark text-white h-11 px-6" data-testid="dashboard-sell-btn"><Link to="/sell"><Plus className="w-4 h-4 mr-1" />New listing</Link></Button>
      </div>
      {user?.account_frozen && (
        <div className="mt-6 flex gap-2 bg-red-50 text-red-800 border border-red-200 rounded-xl p-4 text-sm" data-testid="frozen-banner">
          <AlertCircle className="w-5 h-5 shrink-0" /> Your account is frozen due to a payment dispute. Publishing is disabled. Please contact support.
        </div>
      )}
      <div className="grid sm:grid-cols-3 gap-4 mt-8">
        <PlanCard sub={sub} isDealer={user?.seller_type === "dealer"} />
        <div className="bg-white rounded-2xl border border-navy/10 p-5">
          <div className="flex items-center gap-2 text-slatev text-sm"><Gift className="w-4 h-4" />Cars live</div>
          <div className="font-heading text-xl font-bold mt-2 text-navy" data-testid="spaces-used">{sub ? (sub.active && sub.cap == null ? `${sub.live_count} · unlimited` : `${sub.live_count} of ${sub.cap || 0}`) : "—"}</div>
        </div>
        <div className="bg-white rounded-2xl border border-navy/10 p-5">
          <div className="flex items-center gap-2 text-slatev text-sm"><Tag className="w-4 h-4" />Live listings</div>
          <div className="font-heading text-xl font-bold mt-2 text-navy">{(listings || []).filter((l) => l.status === "live").length}</div>
        </div>
      </div>
      {sub?.active && (
        <AlertDialog>
          <AlertDialogTrigger asChild><Button variant="outline" size="sm" className="mt-4" data-testid="cancel-sub-btn">Cancel plan</Button></AlertDialogTrigger>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader><AlertDialogTitle>Cancel your plan?</AlertDialogTitle>
              <AlertDialogDescription>Your live listings will be taken offline <strong>immediately</strong>. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Keep plan</AlertDialogCancel><AlertDialogAction onClick={cancelSub} className="bg-destructive text-white" data-testid="confirm-cancel-sub">Cancel now</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      <h2 className="font-heading text-2xl font-bold text-navy mt-12 mb-4">Your listings</h2>
      {listings === null ? <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-teal" /></div>
        : listings.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-navy/20 rounded-2xl text-slatev">
            <p>No listings yet.</p>
            <Button asChild className="mt-4 rounded-full bg-teal text-white" data-testid="empty-sell-btn"><Link to="/sell">Create your first listing</Link></Button>
          </div>
        ) : <div className="space-y-3">{listings.map((l) => <ListingRow key={l.id} l={l} act={act} busy={busy} checkout={checkout} />)}</div>}
    </div>
  );
}
